'use client';

/**
 * DiscoveryCostPanel - Discovery token usage & cost dashboard
 *
 * Shows the signed-in user's discovery search costs:
 * - Total conversations, tokens, estimated cost
 * - Per-user breakdown (admin view)
 *
 * @component_role Discovery Cost Insights
 * @business_rule Displays token usage and estimated USD cost from discovery searches.
 *   Data from Conversation node totals, pricing from KlydeConfig model_pricing node.
 *   Regular users see own costs, admin sees all users.
 * @user_outcome Understand AI search costs - how many tokens used, what it costs.
 * @integration_point BFF data gateway → discovery_cost strategy
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Loader2, XCircle, Coins, Zap, MessageSquare, User } from 'lucide-react';
import { SectionHeader } from '../details/shared';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface CostByUser {
  actor_sid: string;
  actor_name: string | null;
  conversations: number;
  total_turns: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  estimated_cost_usd: number | null;
  model: string | null;
}

interface CostLabels {
  title: string;
  searches: string;
  total_tokens: string;
  est_cost: string;
  model: string;
  prompt_input: string;
  completion_output: string;
  by_user: string;
  searches_suffix: string;
  tok_suffix: string;
  no_data: string;
  loading: string;
  error: string;
}

interface CostData {
  days: number;
  total_conversations: number;
  total_tokens: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_estimated_cost_usd: number;
  model: string | null;
  by_user: CostByUser[];
  labels: CostLabels;
}

export function DiscoveryCostPanel() {
  const costStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'cost'));
  const { data, isLoading, error } = useStrategyData<CostData>(
    costStrategy ?? 'discovery_cost',
    { days: 7 },
    true,
  );

  const cost = data?.data;
  const L = cost?.labels;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">{L?.loading ?? 'Loading cost data...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
        <XCircle className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">{L?.error ?? 'Failed to load cost data'}</p>
        <p className="text-xs mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  if (!cost || !L) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Coins className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No cost data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 overflow-auto">
      <SectionHeader icon={<Coins className="w-4 h-4" />} title={`${L.title} (${cost.days}d)`} />

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={<MessageSquare className="w-3.5 h-3.5" />}
          label={L.searches}
          value={cost.total_conversations.toString()}
        />
        <StatCard
          icon={<Zap className="w-3.5 h-3.5" />}
          label={L.total_tokens}
          value={formatTokens(cost.total_tokens)}
        />
        <StatCard
          icon={<Coins className="w-3.5 h-3.5" />}
          label={L.est_cost}
          value={formatCost(cost.total_estimated_cost_usd)}
        />
        <StatCard
          icon={<Zap className="w-3.5 h-3.5 opacity-50" />}
          label={L.model}
          value={cost.model?.replace('gemini-', '').replace('-preview', '') ?? 'n/a'}
        />
      </div>

      <div className="text-xs text-muted-foreground space-y-1 px-1">
        <div className="flex justify-between">
          <span>{L.prompt_input}</span>
          <span>{formatTokens(cost.total_prompt_tokens)}</span>
        </div>
        <div className="flex justify-between">
          <span>{L.completion_output}</span>
          <span>{formatTokens(cost.total_completion_tokens)}</span>
        </div>
      </div>

      {cost.by_user.length > 0 && (
        <>
          <SectionHeader icon={<User className="w-4 h-4" />} title={L.by_user} />
          <div className="space-y-2">
            {cost.by_user.map((u) => (
              <div
                key={u.actor_sid}
                className="flex items-center justify-between px-2 py-1.5 rounded bg-muted/50 text-xs"
              >
                <div>
                  <span className="font-medium">{u.actor_name || u.actor_sid}</span>
                  <span className="text-muted-foreground ml-2">{u.conversations} {L.searches_suffix}</span>
                </div>
                <div className="text-right">
                  <div>{formatTokens(u.total_tokens)} {L.tok_suffix}</div>
                  {u.estimated_cost_usd != null && (
                    <div className="text-muted-foreground">{formatCost(u.estimated_cost_usd)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-panel-border bg-panel p-2.5">
      <div className="text-muted-foreground">{icon}</div>
      <div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(2)}`;
}
